import React from "react";
import dayjs from "dayjs";
import Calendar from "react-calendar";
import "assets/css/MiniCalendar.css";

import { Box, Stack, Typography, Chip, Divider, useTheme } from "@mui/material";

import { CalendarMonth, ArrowForward, Schedule } from "@mui/icons-material";

const toInputDate = (value) => (value ? String(value).split("T")[0] : "");

const DateRangeCalendar = ({
  startDate = "",
  endDate = "",
  dueDate = "",
  onChange,
  minDate = null,
  maxDate = null,

  label = "Project Timeline",

  startLabel = "Start Date",
  endLabel = "Due Date",

  timeframeNotice = "",

  error = "",

  accentColor = "#4F46E5",

  displayFormat = "DD MMM YYYY",
  required = false,
}) => {
  const theme = useTheme();

  const dark = theme.palette.mode === "dark";

  const actualStart = toInputDate(startDate);

  const actualEnd = toInputDate(endDate || dueDate);

  const minDateStr = toInputDate(minDate);

  const maxDateStr = toInputDate(maxDate);

  const duration =
    actualStart && actualEnd
      ? dayjs(actualEnd).diff(dayjs(actualStart), "day") + 1
      : 0;

  const calendarValue = [
    actualStart ? dayjs(actualStart).toDate() : null,
    actualEnd ? dayjs(actualEnd).toDate() : null,
  ];

  const handleCalendarChange = (value) => {
    let start = "";
    let end = "";

    if (Array.isArray(value)) {
      start = value[0] ? dayjs(value[0]).format("YYYY-MM-DD") : "";

      end = value[1] ? dayjs(value[1]).format("YYYY-MM-DD") : start;
    } else {
      start = dayjs(value).format("YYYY-MM-DD");
      end = start;
    }

    onChange?.({
      startDate: start,
      endDate: end,
      due_date: end,

      formattedStart: dayjs(start).format(displayFormat),

      formattedEnd: dayjs(end).format(displayFormat),
    });
  };

  return (
    <Box className="timeline-card">
      {/* Header */}

      <Stack
        sx={{
          width: "100%",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "start",
        }}
        mb={3}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            className="calendar-icon"
            sx={{
              background: accentColor,
            }}
          >
            <CalendarMonth />
          </Box>

          <Box>
            <Typography className="timeline-title">
              {label}
              {required && (
                <span style={{ color: "#ef4444", marginLeft: 4 }}>*</span>
              )}
            </Typography>

            <Typography className="timeline-subtitle">
              Select project duration
            </Typography>
          </Box>
        </Stack>

        {duration > 0 && (
          <Chip
            size="small"
            icon={<Schedule />}
            label={`${duration} ${duration === 1 ? "Day" : "Days"}`}
            className="duration-chip"
            sx={{
              ml: "10px",
              background: accentColor,
            }}
          />
        )}
      </Stack>

      {/* Date Cards */}

      <Stack
        direction={{
          xs: "column",
          md: "row",
        }}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          mt: "10px",
        }}
        spacing={2}
      >
        <DateCard
          title={startLabel}
          value={actualStart}
          accentColor={accentColor}
          displayFormat={displayFormat}
        />

        <ArrowForward
          sx={{
            height: "30px",
            color: dark ? "#94a3b8" : "#94a3b8",
            fontSize: 30,
          }}
        />

        <DateCard
          title={endLabel}
          value={actualEnd}
          accentColor={accentColor}
          displayFormat={displayFormat}
        />
      </Stack>

      <Divider
        sx={{
          my: 3,
          opacity: 0.4,
        }}
      />

      {(timeframeNotice || minDateStr || maxDateStr) && (
        <Typography className="timeline-note">
          {timeframeNotice ||
            `Available between ${dayjs(minDateStr).format(
              displayFormat,
            )} and ${dayjs(maxDateStr).format(displayFormat)}`}
        </Typography>
      )}

      <Box className="calendar-wrapper">
        <Calendar
          selectRange
          value={
            calendarValue[0] && calendarValue[1]
              ? calendarValue
              : calendarValue[0]
          }
          onChange={handleCalendarChange}
          minDate={minDateStr ? dayjs(minDateStr).toDate() : undefined}
          maxDate={maxDateStr ? dayjs(maxDateStr).toDate() : undefined}
        />
      </Box>

      {error && (
        <Typography mt={2} color="error" fontSize={13}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

function DateCard({ title, value, accentColor, displayFormat }) {
  return (
    <Box className="date-card">
      <Typography className="date-card-title">{title}</Typography>
      <Typography className="date-card-value">
        {value ? dayjs(value).format(displayFormat) : "--"}
      </Typography>
      {value && (
        <Typography
          className="date-card-day"
          sx={{
            color: accentColor,
          }}
        >
          {dayjs(value).format("dddd")}
        </Typography>
      )}
    </Box>
  );
}

export default DateRangeCalendar;
